import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryClient';
import { logAction } from '../lib/actionLogger';

export function useVoicePlans() {
  return useQuery({
    queryKey: queryKeys.voicePlans.all,
    queryFn: () => api.get('/voice-plans'),
  });
}

export function useVoicePlan(id) {
  return useQuery({
    queryKey: queryKeys.voicePlans.detail(id),
    queryFn: () => api.get(`/voice-plans/${id}`),
    enabled: !!id,
  });
}

export function useCreateVoicePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/voice-plans', body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.voicePlans.all });
      logAction({
        action: 'Create Voice Plan',
        description: `Created voice plan ${data.title}`,
        details: { id: data.id, title: data.title, status: data.status },
      });
    },
  });
}

export function useUpdateVoicePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...fields }) => api.put(`/voice-plans/${id}`, fields),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.voicePlans.all });
      logAction({
        action: 'Update Voice Plan',
        description: `Updated voice plan ${data.title} (status: ${data.status})`,
        details: { id: data.id, title: data.title, status: data.status },
      });
    },
  });
}

export function useDeleteVoicePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.del(`/voice-plans/${id}`),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.voicePlans.all });
      logAction({
        action: 'Delete Voice Plan',
        description: `Deleted voice plan ${id}`,
        details: { id },
      });
    },
  });
}

export function useAddPlanTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ planId, ...body }) => api.post(`/voice-plans/${planId}/tasks`, body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.voicePlans.all });
      logAction({
        action: 'Add Plan Task',
        description: `Added task ${data.title} to voice plan ${data.plan_id}`,
        details: { id: data.id, planId: data.plan_id, title: data.title },
      });
    },
  });
}

export function useUpdatePlanTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ planId, taskId, ...fields }) => api.put(`/voice-plans/${planId}/tasks/${taskId}`, fields),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.voicePlans.all }),
  });
}

export function useDeletePlanTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ planId, taskId }) => api.del(`/voice-plans/${planId}/tasks/${taskId}`),
    onSuccess: (_data, { planId, taskId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.voicePlans.all });
      logAction({
        action: 'Delete Plan Task',
        description: `Removed task ${taskId} from voice plan ${planId}`,
        details: { planId, taskId },
      });
    },
  });
}

export function useCompletePlanTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ planId, taskId, doneNote }) =>
      api.post(`/voice-plans/${planId}/tasks/${taskId}/complete`, { done_note: doneNote }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.voicePlans.all });
      logAction({
        action: 'Complete Plan Task',
        description: `Completed task ${data.title}`,
        details: { id: data.id, planId: data.plan_id, title: data.title, doneNote: data.done_note },
      });
    },
  });
}

export function useSummarizePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planId) => api.post(`/voice-plans/${planId}/summarize`),
    onSuccess: (_data, planId) => {
      qc.invalidateQueries({ queryKey: queryKeys.voicePlans.all });
      logAction({
        action: 'AI Analyze Voice Plan',
        description: `Ran AI analysis on voice plan ${planId}`,
        details: { id: planId },
      });
    },
  });
}

export function useQueuePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planId) => api.post(`/voice-plans/${planId}/queue`),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.voicePlans.all });
      logAction({
        action: 'Queue Voice Plan',
        description: `Queued voice plan ${data.id} for chat planning`,
        details: { id: data.id, queuedAt: data.queued_at },
      });
    },
  });
}
